import Header from '../components/Header';
import Footer from '../components/Footer';
import { Rocket, ShieldCheck, Zap, ArrowRight, TrendingUp, Users, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-32 lg:pt-24 lg:pb-48 overflow-hidden">
        <div className="absolute inset-0 bg-stripe-bg -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[140%] h-full bg-white rounded-[100%] shadow-[0_0_120px_rgba(255,38,0,0.06)]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative text-center lg:text-left">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="space-y-12 animate-in fade-in slide-in-from-left-10 duration-700">
              <div className="inline-flex items-center gap-3 px-4 py-2 bg-primary-50 rounded-full border border-primary-100">
                <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
                <span className="text-xs font-bold text-primary-600 uppercase tracking-widest">New: Quick SME Funding</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black text-stripe-dark leading-[1.05] tracking-tight">
                Funding the <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-orange-500">future</span> of your business.
              </h1>
              
              <p className="max-w-xl mx-auto lg:mx-0 text-xl text-stripe-slate leading-relaxed">
                Connect your business to the capital it needs. From daily quick loans to enterprise-scale expansion, Finova empowers your journey with transparent and instant financing.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center gap-6 justify-center lg:justify-start pt-6">
                <Link to="/register" className="btn-primary px-10 py-5 rounded-full text-base shadow-xl hover:shadow-primary-500/20 active:scale-95 transition-all">
                  Get Started for Free
                  <ArrowRight size={20} />
                </Link>
                <Link to="/contact" className="btn-secondary px-10 py-5 rounded-full text-base font-bold">
                  Contact Sales
                </Link>
              </div>
              

            </div>

            {/* Visual Part */}
            <div className="relative animate-in zoom-in duration-1000">
               <div className="relative z-10 p-2 bg-white rounded-[40px] shadow-2xl border border-stripe-border overflow-hidden">
                  <img 
                    src="/assets/images/success_members.png" 
                    alt="Finova Success Community" 
                    className="w-full h-auto aspect-square object-cover rounded-[32px]"
                  />
                  <div className="absolute bottom-6 left-6 right-6 p-6 bg-white/90 backdrop-blur-md rounded-2xl border border-white shadow-lg space-y-1">
                     <p className="text-[10px] font-black text-primary-500 uppercase tracking-widest">Active Members</p>
                     <p className="text-sm font-black text-stripe-dark leading-none">Financing Ambition Nationwide</p>
                  </div>
               </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl -z-10" />
              <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-24 space-y-6">
            <h2 className="text-base font-bold text-primary-500 uppercase tracking-widest leading-none">Why Finova?</h2>
            <p className="text-4xl font-black text-stripe-dark tracking-tighter">Financing built for the digital age.</p>
            <p className="text-lg text-stripe-slate font-medium leading-relaxed">We've removed the legacy bottlenecks of traditional banking to bring you capital at the speed of your business.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { title: 'Instant Processing', desc: 'Our AI-driven protocol evaluates your application in real-time. Capital in minutes, not days.', icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-50' },
              { title: 'Finance Made Easy', desc: 'Simplified documentation and instant processing to ensure you get funds without any hassle.', icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-50' },
              { title: 'Scale with Ease', desc: 'As you grow, so does your credit limit. We rewards consistency with better rates and higher capital.', icon: Rocket, color: 'text-primary-500', bg: 'bg-primary-50' }
            ].map((f, i) => (
              <div key={i} className="s-card group hover:-translate-y-2 transition-transform duration-300">
                <div className={`w-14 h-14 ${f.bg} rounded-2xl flex items-center justify-center ${f.color} mb-8 shadow-sm group-hover:scale-110 transition-transform`}>
                  <f.icon size={28} />
                </div>
                <h3 className="text-xl font-bold text-stripe-dark tracking-tight mb-4">{f.title}</h3>
                <p className="text-stripe-slate text-sm font-medium leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6">
         <div className="bg-primary-500 rounded-[48px] p-12 lg:p-24 relative overflow-hidden group">
            <div className="relative z-10 text-center space-y-10">
               <h2 className="text-4xl lg:text-6xl font-black text-white tracking-tighter max-w-3xl mx-auto">
                 Ready to make your finance easy with us?
               </h2>
               <p className="text-white/80 text-lg lg:text-xl font-medium max-w-2xl mx-auto leading-relaxed">
                 Join thousands of growing companies who trust Finova for their capital requirements. No hidden fees. No paperwork.
               </p>
               <div className="flex flex-col sm:flex-row items-center gap-6 justify-center">
                  <Link to="/register" className="px-12 py-5 bg-white text-primary-500 font-black rounded-full shadow-lg hover:scale-105 active:scale-95 transition-all text-base">
                    Quick Application
                  </Link>
                  <Link to="/contact" className="px-12 py-5 bg-primary-600 text-white font-black rounded-full shadow-lg border border-primary-400 hover:bg-primary-700 transition-all text-base">
                    Talk to an Advisor
                  </Link>
               </div>
            </div>
            
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-black/5 rounded-full translate-y-1/2 -translate-x-1/2" />
         </div>
      </section>

      <Footer />
    </div>
  );
}
